/**
 * Step 7 — narrative-trigger evaluation (R11), the stage architecture.md
 * §7.2 reserved from the founding and §7.3 sketched as small matchers.
 *
 * @remarks
 * Runs after the scheduler tick on every EXECUTED turn: time moves first,
 * then the watchers look at what the turn — action and tick together — made
 * true. A trigger fires when every condition holds and it has never fired
 * this session (`narrative.firedTriggerIds` is the edge guard) — unless it is
 * `repeatable`, which narrows that guard to this sweep (D119); its effects
 * run through the SAME `applyEffect` as handlers and events, so everything
 * an action can do, a watcher can do — including concluding the story.
 *
 * **The sweep re-selects after every firing**, exactly like the tick's chain
 * loop: a trigger's effects may make another trigger's conditions true, and
 * the beat should land this turn, not next. Selection is pack declaration
 * order, which makes chains deterministic; termination is by construction —
 * each pass either fires a trigger that cannot fire again IN THIS SWEEP, or
 * stops.
 *
 * D119: `repeatable` triggers narrow that guard rather than removing it. A
 * once-per-session ledger is right for a line she delivers once and wrong for
 * a device, which is used repeatedly by definition; a repeatable trigger is
 * exempt from `narrative.firedTriggerIds` and retires into a per-sweep set
 * instead. The termination argument is unchanged — the eligible set still
 * shrinks on every pass — only the ledger it shrinks against is per-turn.
 *
 * A trigger whose conditions hold at genesis fires on the first executed
 * turn: "start it at the beginning" is authorable at last (Calder's
 * 2026-08-22 §4.3), and so is every standing watcher his pump-plus-sensor
 * pattern (§4.1) had to fake with polling events.
 *
 * @packageDocumentation
 */

import type { OccurrenceMatcher, RuntimeState, RuntimeTranscriptEntry, TriggerId } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { TranscriptCursor } from '../execution/transcript-cursor.js';

import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { applyEffects } from '../effects/apply-effect.js';
import { isExecutedTurnFamily, type OccurrenceFact } from './occurrences.js';

/** What one evaluation pass did: entries spoken, cursor advanced, fired ids, and the state after. */
export interface TriggerEvaluationResult {
	/** The advanced cursor, for anything stamped after this stage. */
	readonly cursor: TranscriptCursor;
	/** Transcript entries the fired triggers spoke, in firing order. */
	readonly entries: readonly RuntimeTranscriptEntry[];
	/** The triggers that fired this turn and are now RETIRED for the session — a `repeatable` trigger (D119) never joins this list. */
	readonly firedTriggerIds: readonly TriggerId[];
	/** Every trigger this sweep fired, repeatable or not — the ledger a later sweep IN THE SAME TURN continues (D188). */
	readonly firedThisSweep: ReadonlySet<TriggerId>;
	readonly state: RuntimeState;
}

/**
 * Evaluate every standing trigger against the turn's outcome (step 7).
 *
 * @param bundle - The loaded bundle (trigger definitions, condition context)
 * @param state - The state after the action's effects and the scheduler tick
 * @param cursor - The turn's transcript cursor, already past the tick's entries
 * @param occurrence - What just happened at this evaluation site
 * @param firedEarlierThisTurn - The ledger of an earlier sweep this turn (D188: the end-of-turn tick's sweep continues step 7's, so a repeatable trigger still fires at most once per turn)
 * @returns The state after every fired trigger, the entries spoken, and the fired ids
 */
export const evaluateTriggers = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	occurrence: OccurrenceFact,
	firedEarlierThisTurn: ReadonlySet<TriggerId> = new Set(),
): TriggerEvaluationResult => {
	const firedTriggerIds: TriggerId[] = [];
	const entries: RuntimeTranscriptEntry[] = [];

	let workingState = state;
	let workingCursor = cursor;

	// D119: repeatable triggers are exempt from the SESSION ledger, so this
	// sweep keeps its own. Termination is unchanged in shape — the eligible set
	// still shrinks on every pass — but a device's arm may fire again next turn.
	const firedThisSweep = new Set<TriggerId>(firedEarlierThisTurn);

	// Re-select after every firing (chains land this turn); the guards below are
	// also the termination proof — `narrative.firedTriggerIds` for a beat that
	// happens once, `firedThisSweep` for one that happens every time.
	for (;;) {
		const eligibleTrigger = bundle.contentPack.triggers.find(
			(trigger) =>
				(trigger.repeatable === true
					? !firedThisSweep.has(trigger.triggerId)
					: !workingState.narrative.firedTriggerIds.includes(trigger.triggerId)) &&
				isListeningFor(trigger.on, occurrence) &&
				// D226 (E19): an OCCURRENCE watcher reads the command that raised
				// the occurrence; a state watcher has none in hand (nobody typed
				// anything in particular — the world changed), and the loader
				// refuses it the conditions that would ask.
				trigger.conditions.every((condition) =>
					evaluateCondition(condition, bundle, workingState, trigger.on === undefined ? undefined : occurrence.commandInHand),
				),
		);

		if (eligibleTrigger === undefined) {
			return { cursor: workingCursor, entries, firedThisSweep, firedTriggerIds, state: workingState };
		}

		// Retire BEFORE applying effects, so a trigger whose own effects keep
		// its conditions true cannot re-select itself mid-sweep. A repeatable
		// trigger retires into THIS SWEEP only (D119) — it stays out of the
		// session ledger so the same arm is available again next turn.
		firedThisSweep.add(eligibleTrigger.triggerId);

		if (eligibleTrigger.repeatable !== true) {
			firedTriggerIds.push(eligibleTrigger.triggerId);
			workingState = {
				...workingState,
				narrative: {
					...workingState.narrative,
					firedTriggerIds: [...workingState.narrative.firedTriggerIds, eligibleTrigger.triggerId],
				},
			};
		}

		// D246: an occurrence watcher's effects answer the same sentence its conditions read — a branch among them reads it too.
		const applied = applyEffects(
			eligibleTrigger.effects,
			bundle,
			workingState,
			workingCursor,
			`trigger:${eligibleTrigger.triggerId}`,
			eligibleTrigger.on === undefined ? undefined : occurrence.commandInHand,
		);

		workingState = applied.state;
		workingCursor = applied.cursor;
		entries.push(...applied.entries);
	}
};

/**
 * Whether one trigger listens for this occurrence (R12).
 *
 * @remarks
 * A STATE watcher (no `on`) listens only at step 7 — the executed-turn
 * evaluation — because its edge is "the world changed," and only executed
 * turns change the world; since D235 an executed turn is a command OR an
 * answer to an actor's question (`isExecutedTurnFamily`). An OCCURRENCE
 * watcher listens for its own family, narrowed by its optional
 * discriminant — a `dialogue_answered` by the prompt, and within it the
 * option.
 *
 * @param matcher - The trigger's `on`, if any
 * @param occurrence - What just happened at this evaluation site
 * @returns Whether the trigger is eligible here
 */
const isListeningFor = (matcher: OccurrenceMatcher | undefined, occurrence: OccurrenceFact): boolean => {
	if (matcher === undefined) {
		return isExecutedTurnFamily(occurrence.family);
	}

	switch (matcher.type) {
		case 'command_executed': {
			return occurrence.family === 'command_executed' && (matcher.intent === undefined || matcher.intent === occurrence.detail);
		}
		case 'command_rejected': {
			return occurrence.family === 'command_rejected' && (matcher.reason === undefined || matcher.reason === occurrence.detail);
		}
		case 'dialogue_answered': {
			return (
				occurrence.family === 'dialogue_answered' &&
				(matcher.promptId === undefined || matcher.promptId === occurrence.detail) &&
				(matcher.optionId === undefined || matcher.optionId === occurrence.optionId)
			);
		}
		case 'parse_failed': {
			return (
				occurrence.family === 'parse_failed' && (matcher.failureKind === undefined || matcher.failureKind === occurrence.detail)
			);
		}
		case 'reaction_spoken': {
			return (
				occurrence.family === 'reaction_spoken' &&
				(matcher.reactionKey === undefined || matcher.reactionKey === occurrence.detail)
			);
		}
	}
};
